// vendor libs
import * as Cookie from 'app/packages/cookie-utils';

const storage = {};

/**
 * Simple session storage implementation based off of window.sessionStorage that
 * supports browsers that don't support window.sessionStorage, specifically private
 * browsing in safari. This uses a cookie as the backup and does
 * *not* take into consideration cookie size limitation.
 */

/**
 * @param {string} cookieKey the name of the cookie to use for storage
 * @constructor
 * @implements {IStorage}
 */
const CookieBackedStorage = function(cookieKey) {
  this._cookieKey = cookieKey;
};
Object.assign(
  CookieBackedStorage.prototype,
  /** @lends {CookieBackedStorage.prototype} */ {
    /**
     * @override
     */
    length() {
      // Note in window.sessionStorage this is an attribute
      const cookie = Cookie.get(this._cookieKey);
      const data = /** @type {Object} */ (cookie ? JSON.parse(cookie) : {});

      return Object.keys(data).length;
    },
    /**
     * @override
     */
    key(index) {
      throw new Error('intentionally unimplemented function');
      // TODO(nadine): implement.
    },
    /**
     * @override
     */
    getItem(key) {
      const cookie = Cookie.get(this._cookieKey);
      const data = cookie ? JSON.parse(cookie) : {};
      return data[key];
    },
    /**
     * @override
     */
    setItem(key, value) {
      const cookie = Cookie.get(this._cookieKey);
      const data = cookie ? JSON.parse(cookie) : {};
      data[key] = value;
      Cookie.set(this._cookieKey, JSON.stringify(data));
    },
    /**
     * @override
     */
    removeItem(key) {
      const cookie = Cookie.get(this._cookieKey);
      const data = cookie ? JSON.parse(cookie) : {};
      delete data[key];
      Cookie.set(this._cookieKey, JSON.stringify(data));
    },
    /**
     * @override
     */
    prependItem(key, value, max) {
      const storageItem = this.getItem(key);
      const list = Array.isArray(storageItem) ? storageItem : [];
      list.unshift(value);
      if (list.length > max) {
        list.pop();
      }
      this.setItem(key, list);
    },
    /**
     * @override
     */
    clear() {
      Cookie.unset(this._cookieKey);
    },
  },
);

/**
 * @constructor
 * @implements {IStorage}
 */
const SessionStorage = function() {};
/** @override */
SessionStorage.prototype.length = function() {
  // Note in window.sessionStorage this is an attribute
  return window.sessionStorage.length;
};
/** @override */
SessionStorage.prototype.key = function(index) {
  throw new Error('intentionally unimplemented function');
  // return window.sessionStorage.key(index);
};
/** @override */
SessionStorage.prototype.getItem = function(key) {
  const storageItem = window.sessionStorage.getItem(key);
  return storageItem !== null ? JSON.parse(storageItem) : null;
};
/** @override */
SessionStorage.prototype.setItem = function(key, value) {
  window.sessionStorage.setItem(key, JSON.stringify(value));
};
/** @override */
SessionStorage.prototype.removeItem = function(key) {
  window.sessionStorage.removeItem(key);
};
/** @override */
SessionStorage.prototype.prependItem = function(key, value, max) {
  const storageItem = window.sessionStorage.getItem(key);
  const list = storageItem ? JSON.parse(storageItem) : [];
  list.unshift(value);
  if (list.length > max) {
    list.pop();
  }
  window.sessionStorage.setItem(key, JSON.stringify(list));
};
/** @override */
SessionStorage.prototype.clear = function() {
  window.sessionStorage.clear();
};

/**
 * @constructor
 * @implements {IStorage}
 */
const LocalStorage = function() {};
Object.assign(
  LocalStorage.prototype,
  /** @lends {LocalStorage.prototype} */ {
    /**
     * @override
     */
    length() {
      // Note in window.localStorage this is an attribute
      return window.localStorage.length;
    },
    /**
     * @override
     */
    key(index) {
      throw new Error('intentionally unimplemented function');
    },
    /**
     * @override
     */
    getItem(key) {
      const storageItem = window.localStorage.getItem(key);
      return storageItem !== null ? JSON.parse(storageItem) : storageItem;
    },
    /**
     * @override
     */
    setItem(key, value) {
      window.localStorage.setItem(key, JSON.stringify(value));
    },
    /**
     * @override
     */
    removeItem(key) {
      window.localStorage.removeItem(key);
    },
    /**
     * @override
     */
    prependItem(key, value, max) {
      const storageItem = window.localStorage.getItem(key);
      const list = storageItem ? JSON.parse(storageItem) : [];
      list.unshift(value);
      if (list.length > max) {
        list.pop();
      }
      window.localStorage.setItem(key, JSON.stringify(list));
    },
    /**
     * @override
     */
    clear() {
      window.localStorage.clear();
    },
  },
);

/**
 * Retrieve a storage instance
 * @param {boolean} isSession
 * @return {!IStorage}
 */
const createStorage = function(isSession) {
  // We need to use the try/catch because IE10 will throw an ACCESS DENIED
  //  error if `localstorage` doesn't exist in window.
  let windowFn;
  try {
    windowFn = isSession ? window.sessionStorage : window.localStorage;
  } catch (e) {
    windowFn = null;
  }

  const cookieKey = isSession ? PCONST.COOKIE_SESSION_STORAGE : PCONST.COOKIE_LOCAL_STORAGE;

  let needPolyfill = false;
  const storage = windowFn;
  if (!storage) {
    needPolyfill = true;
  } else {
    try {
      storage.setItem('testing', 'test');
      storage.removeItem('testing');
    } catch (e) {
      // TODO(chris): This testing of quota exceeded seems insufficient.
      // See: http://chrisberkhout.com/blog/localstorage-errors/.
      if (e.code === window.DOMException.QUOTA_EXCEEDED_ERR && storage.length === 0) {
        needPolyfill = true;
      } else {
        throw e;
      }
    }
  }

  if (needPolyfill) {
    return new CookieBackedStorage(cookieKey);
  }
  if (isSession) {
    return new SessionStorage();
  }
  return new LocalStorage();
};

const createKey = function(storageKeyBaseString, storageKeyPrefixes = []) {
  return [...storageKeyPrefixes, storageKeyBaseString].join('_');
};

export const sessionStorage = createStorage(true);
export const localStorage = createStorage(false);
export { createKey };

storage.sessionStorage = sessionStorage;
storage.localStorage = localStorage;
storage.createKey = createKey;

export default storage;
